import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EnhancedAvatar } from "@/components/ui/enhanced-avatar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, LogOut, Rocket, User } from "lucide-react";
import { formatPhoneNumber } from "@/lib/format";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface UserMenuProps {
  isParentPortal?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isParentPortal = false }) => {
  const { user, signOut, refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State for form fields
  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || '');
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.user_metadata?.phone || '');
  const [formattedPhone, setFormattedPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Elegant reset for Getting Started tips (dropdown version)
  const [showResetGettingStarted, setShowResetGettingStarted] = useState(() => {
    if (typeof window !== 'undefined' && user?.id) {
      return !!localStorage.getItem(`hideGettingStarted_${user.id}`);
    }
    return false;
  });
  const handleResetGettingStarted = () => {
    if (user?.id) {
      localStorage.removeItem(`hideGettingStarted_${user.id}`);
      setShowResetGettingStarted(false);
      toast.success("Getting Started tips will be shown again next time you visit the dashboard.");
    }
  };
  
  // Update form fields when user data changes
  useEffect(() => {
    const loadUserProfile = async () => {
      if (user?.id) {
        try {
          // Try to load from user_profiles table first
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .maybeSingle();
          
          if (!error && profile) {
            // Use user_profiles data if available
            setFirstName(profile.first_name || '');
            setLastName(profile.last_name || '');
          } else {
            // Fallback to auth metadata
            setFirstName(user.user_metadata?.first_name || '');
            setLastName(user.user_metadata?.last_name || '');
          }
          
          // Phone is stored in auth metadata
          const phone = user.user_metadata?.phone || '';
          setPhoneNumber(phone);
          setFormattedPhone(phone ? formatPhoneNumber(phone) : '');
        } catch (error) {
          // Fallback to auth metadata on any error
          setFirstName(user.user_metadata?.first_name || '');
          setLastName(user.user_metadata?.last_name || '');
          const phone = user.user_metadata?.phone || '';
          setPhoneNumber(phone);
          setFormattedPhone(phone ? formatPhoneNumber(phone) : '');
        }
      }
    };
    
    loadUserProfile();
  }, [user]);
  
  // Get user info
  const userEmail = user?.email || '';
  const userName = firstName && lastName 
    ? `${firstName} ${lastName}`
    : user?.user_metadata?.full_name || user?.user_metadata?.name || userEmail.split('@')[0] || 'User';
  
  // Generate initials from loaded firstName and lastName if available, otherwise from userName
  const userInitials = firstName && lastName
    ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    : userName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
  
  const avatarUrl = user?.user_metadata?.avatar_url;

  // Password validation
  const isPasswordValid = (password: string) => {
    return password.length >= 8 && 
           /[A-Z]/.test(password) && 
           /[a-z]/.test(password) && 
           /\d/.test(password);
  };

  const passwordHints = [
    { text: "At least 8 characters", valid: newPassword.length >= 8 },
    { text: "One uppercase letter", valid: /[A-Z]/.test(newPassword) },
    { text: "One lowercase letter", valid: /[a-z]/.test(newPassword) },
    { text: "One number", valid: /\d/.test(newPassword) }
  ];

  // Handle avatar upload
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUpdating(true);
      
      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}-${Math.random()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('user-avatars')
        .upload(fileName, file);

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(data.path);

      // Use Supabase image transformation for optimized avatars
      const transformedUrl = `${publicUrl}?width=200&height=200&resize=cover&format=webp`;

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { avatar_url: transformedUrl }
      });

      if (updateError) throw updateError;

      toast.success('Profile picture updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile picture');
      console.error('Avatar upload error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle phone input changes
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    setPhoneNumber(formatted);
  };

  // Handle name updates
  const handleNameUpdate = async () => {
    if (!user) return;

    try {
      setIsUpdating(true);
      
      // Update auth metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { 
          first_name: firstName,
          last_name: lastName
        }
      });

      if (authError) throw authError;

      // Also update user_profiles table if it exists
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          first_name: firstName,
          last_name: lastName
        })
        .eq('id', user.id);

      // Don't throw on profile error as auth metadata is primary
      if (profileError) {
        console.warn('Failed to update user_profiles:', profileError);
      }

      toast.success('Name updated successfully!');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error('Failed to update name');
      console.error('Name update error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle phone number update
  const handlePhoneUpdate = async () => {
    if (!user) return;

    try {
      setIsUpdating(true);
      
      // Update auth metadata (primary storage for phone)
      const { error } = await supabase.auth.updateUser({
        data: { phone: phoneNumber }
      });

      if (error) throw error;

      toast.success('Phone number updated successfully!');
    } catch (error) {
      toast.error('Failed to update phone number');
      console.error('Phone update error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      toast.error('New password does not meet requirements');
      return;
    }

    try {
      setIsUpdating(true);

      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: currentPassword,
      });

      if (signInError) {
        toast.error('Current password is incorrect');
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      toast.success('Password updated successfully!');
    } catch (error) {
      toast.error('Failed to update password');
      console.error('Password update error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };
  
  // For parent portal users, just navigate to profile page
  if (isParentPortal) {
    return (
      <Button 
        variant="ghost" 
        className="relative h-8 w-8 rounded-full p-0" 
        asChild
      >
        <Link to="/parent-portal/profile">
          <EnhancedAvatar
            src={avatarUrl}
            firstName={firstName}
            lastName={lastName}
            userName={userName}
            size="sm"
          />
        </Link>
      </Button>
    );
  }

  // For admin/staff users, navigate directly to profile page (matching parent portal pattern)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
          <Link to="/profile">
            <EnhancedAvatar
              src={avatarUrl}
              firstName={firstName}
              lastName={lastName}
              userName={userName}
              size="sm"
            />
          </Link>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 p-1">
        <DropdownMenuLabel className="px-2 py-1.5">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userName}</p>
            <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <Link to="/profile" className="block">
          <Button
            variant="ghost" 
            className="w-full justify-start gap-2 h-8 px-2"
          >
            <User className="h-4 w-4" />
            View Profile
          </Button>
        </Link>
        
        <DropdownMenuSeparator />
        
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="w-full justify-start gap-2 h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
